import {
  ISaveScenarioNode,
  IScenarioConfig,
  IScenarioNode,
  ScenarioAction,
  ScenarioFormFieldType,
  ScenarioNodeType,
} from "../models/ILiveChatScenario";

/* ------------------------------- Görünüm ------------------------------- */

export const TYPE_META: Record<ScenarioNodeType, { label: string; icon: string; color: string; bg: string }> = {
  Button: { label: "Buton",     icon: "bi-hand-index-thumb-fill", color: "#4f46e5", bg: "#eef2ff" },
  Text:   { label: "Cevap",     icon: "bi-chat-left-text-fill",   color: "#0284c7", bg: "#e0f2fe" },
  Otp:    { label: "Doğrulama", icon: "bi-shield-lock-fill",      color: "#d97706", bg: "#fef3c7" },
  Form:   { label: "Form",      icon: "bi-ui-checks",             color: "#7c3aed", bg: "#f3e8ff" },
  Ticket: { label: "Ticket",    icon: "bi-ticket-perforated-fill", color: "#e11d48", bg: "#ffe4e6" },
};

export const ACTION_META: Record<ScenarioAction, { label: string; icon: string; color: string; bg: string } | null> = {
  None: null,
  HandoffToAgent:    { label: "Temsilciye bağlar", icon: "bi-headset",        color: "#0f766e", bg: "#ccfbf1" },
  CreateTicket:      { label: "Ticket açar",       icon: "bi-ticket-fill",    color: "#be123c", bg: "#ffe4e6" },
  CloseConversation: { label: "Sohbeti kapatır",   icon: "bi-door-closed-fill", color: "#475569", bg: "#f1f5f9" },
};

export const FIELD_TYPES: { value: ScenarioFormFieldType; label: string }[] = [
  { value: "text", label: "Kısa metin" },
  { value: "textarea", label: "Uzun metin" },
  { value: "phone", label: "Telefon" },
  { value: "email", label: "E-posta" },
  { value: "number", label: "Sayı" },
  { value: "select", label: "Seçim listesi" },
];

export const TICKET_PRIORITIES = [
  { value: 1, label: "Düşük" },
  { value: 2, label: "Orta" },
  { value: 3, label: "Yüksek" },
  { value: 4, label: "Acil" },
];

/** Bu türler ve aksiyon, gönderim sınırına (throttle) tabidir. */
export const opensTicket = (type: ScenarioNodeType, action: ScenarioAction) =>
  type === "Form" || type === "Ticket" || action === "CreateTicket";

/* ------------------------------ ConfigJson ------------------------------ */

export function parseConfig(json?: string | null): IScenarioConfig {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function isEmpty(v: unknown): boolean {
  if (v === undefined || v === null || v === "") return true;
  if (Array.isArray(v)) return false;
  if (typeof v === "object") return Object.keys(v as object).length === 0;
  return false;
}

/** Boş alt nesneleri ve boş değerleri temizler. */
function prune(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.entries(obj).forEach(([k, v]) => {
    const value = v && typeof v === "object" && !Array.isArray(v) ? prune(v as Record<string, unknown>) : v;
    if (!isEmpty(value)) out[k] = value;
  });
  return out;
}

/**
 * Kaydedilecek ConfigJson. Adım türüne uymayan bölümler atılır (Form'dan Buton'a çevrilen
 * adımın eski alan listesi taşınmasın); panelin tanımadığı anahtarlar korunur.
 */
export function buildConfigJson(config: IScenarioConfig, type: ScenarioNodeType, action: ScenarioAction): string | null {
  const copy: Record<string, unknown> = { ...config };
  if (type !== "Otp") delete copy.otp;
  if (type !== "Form") delete copy.form;
  if (!opensTicket(type, action)) {
    delete copy.ticket;
    delete copy.throttle;
  }
  if (copy.requiresOrder === false) delete copy.requiresOrder;
  const cleaned = prune(copy);
  return Object.keys(cleaned).length ? JSON.stringify(cleaned) : null;
}

/* -------------------------------- Ağaç --------------------------------- */

export interface IFlatNode {
  node: IScenarioNode;
  depth: number;
  /** Kökten bu adıma kadar metinler */
  path: string[];
  /** Kendisi ya da bir üstü pasif mi (müşteriye görünmez) */
  hidden: boolean;
}

export function flattenTree(nodes: IScenarioNode[], depth = 0, path: string[] = [], parentHidden = false): IFlatNode[] {
  const out: IFlatNode[] = [];
  sortNodes(nodes).forEach((n) => {
    const hidden = parentHidden || !n.isActive;
    const p = [...path, n.text];
    out.push({ node: n, depth, path: p, hidden });
    out.push(...flattenTree(n.children ?? [], depth + 1, p, hidden));
  });
  return out;
}

export const sortNodes = (nodes: IScenarioNode[]) =>
  nodes.slice().sort((a, b) => a.sortOrder - b.sortOrder || a.text.localeCompare(b.text, "tr"));

export function findNode(nodes: IScenarioNode[], id: string): IScenarioNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const hit = findNode(n.children ?? [], id);
    if (hit) return hit;
  }
  return null;
}

/** Bir adımın kardeşleri (kendisi dahil), sıralı. */
export function siblingsOf(tree: IScenarioNode[], parentId: string | null | undefined): IScenarioNode[] {
  if (!parentId) return sortNodes(tree);
  return sortNodes(findNode(tree, parentId)?.children ?? []);
}

export function descendantIds(node: IScenarioNode): Set<string> {
  const ids = new Set<string>();
  const walk = (n: IScenarioNode) => (n.children ?? []).forEach((c) => { ids.add(c.id); walk(c); });
  walk(node);
  return ids;
}

/** Adımın altındaki en derin yaprağa kadar seviye sayısı (yaprak = 0). */
export function subtreeHeight(node: IScenarioNode): number {
  const kids = node.children ?? [];
  return kids.length ? 1 + Math.max(...kids.map(subtreeHeight)) : 0;
}

/** Mevcut adımı PUT gövdesine çevirir (taşıma gibi tek alan değişen güncellemeler için). */
export function nodeToPayload(node: IScenarioNode): ISaveScenarioNode {
  return {
    parentId: node.parentId ?? null,
    text: node.text,
    content: node.content ?? null,
    nodeType: node.nodeType,
    action: node.action,
    topic: node.topic ?? null,
    sortOrder: node.sortOrder,
    isActive: node.isActive,
    languageCode: node.languageCode || "tr",
    code: node.code ?? null,
    configJson: node.configJson ?? null,
  };
}

export function countNodes(nodes: IScenarioNode[]): number {
  return nodes.reduce((sum, n) => sum + 1 + countNodes(n.children ?? []), 0);
}

/* ------------------------------ Token'lar ------------------------------ */

export const TOKEN_LABELS: Record<string, string> = {
  "customer.name": "Müşteri adı",
  "customer.fullName": "Ad soyad",
  "conversation.no": "Sohbet no",
  "order.no": "Sipariş no",
  "order.status": "Sipariş durumu",
  "order.restaurant": "Restoran",
  "order.price": "Tutar",
  "order.date": "Sipariş zamanı",
  "order.estimatedDeliveryAt": "Tahmini teslim saati",
  "order.estimatedMinutes": "Teslim süresi (dk)",
  "order.remainingMinutes": "Kalan süre (dk)",
};

export const SAMPLE_TOKEN_VALUES: Record<string, string> = {
  "customer.name": "Deniz",
  "customer.fullName": "Deniz Yılmaz",
  "conversation.no": "CHT-260824-ABC123",
  "order.no": "SP-104582",
  "order.status": "Yolda",
  "order.restaurant": "Mutfacik Kebap",
  "order.price": "349,50",
  "order.date": "24.08.2026 19:40",
  "order.estimatedDeliveryAt": "20:25",
  "order.estimatedMinutes": "45",
  "order.remainingMinutes": "25",
};

const TOKEN_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

export const hasTokens = (text?: string | null) => !!text && /\{\{\s*[\w.]+\s*\}\}/.test(text);

/**
 * Backend'deki çözümlemenin önizlemesi: sipariş bağlı değilse order.* token'ları çözülemez;
 * o durumda varsa fallback gönderilir, yoksa token'lar silinir.
 */
export function resolvePreview(content: string | null | undefined, config: IScenarioConfig, orderLinked: boolean): string | null {
  if (!content || !content.trim()) return null;
  let unresolved = false;
  const fill = (text: string) =>
    text.replace(TOKEN_RE, (_, key: string) => {
      if (key.startsWith("order.") && !orderLinked) { unresolved = true; return ""; }
      const v = SAMPLE_TOKEN_VALUES[key];
      if (v === undefined) { unresolved = true; return ""; }
      return v;
    });
  const resolved = fill(content);
  if (!unresolved && !(config.requiresOrder && !orderLinked)) return resolved;
  if (config.fallbackContent && config.fallbackContent.trim()) {
    return config.fallbackContent.replace(TOKEN_RE, (_, key: string) =>
      key.startsWith("order.") && !orderLinked ? "" : SAMPLE_TOKEN_VALUES[key] ?? "");
  }
  return resolved.replace(/\s{2,}/g, " ").trim();
}

/* ------------------------------ Yardımcılar ------------------------------ */

/** "Restoran adı" → "restoranAdi" — form alanı anahtarı için. */
export function toFieldName(label: string): string {
  const map: Record<string, string> = { ç: "c", ğ: "g", ı: "i", İ: "i", ö: "o", ş: "s", ü: "u", Ç: "c", Ğ: "g", Ö: "o", Ş: "s", Ü: "u" };
  const ascii = label.replace(/[çğıİöşüÇĞÖŞÜ]/g, (c) => map[c] ?? c).replace(/[^a-zA-Z0-9 ]/g, " ").trim();
  const words = ascii.split(/\s+/).filter(Boolean);
  return words
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join("");
}

const ERROR_TEXT: Record<string, string> = {
  NodeNotFound: "Adım bulunamadı. Liste yenilendi.",
  ParentNotFound: "Seçilen üst adım artık yok.",
  ParentWouldCreateCycle: "Bir adım kendi alt adımının altına taşınamaz.",
  DepthLimitReached: "Senaryo bu kadar derinleşemez.",
  CodeAlreadyExists: "Bu kod başka bir adımda kullanılıyor.",
  InvalidConfigJson: "Gelişmiş ayarlar (JSON) geçersiz.",
  NodeHasChildren: "Bu adımın alt adımları var.",
};

export function scenarioErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number; data?: unknown } };
  if (e?.response?.status === 403) return "Bu işlem için SuperAdmin yetkisi gerekiyor.";
  const data = e?.response?.data;
  const first = Array.isArray(data) ? data[0] : null;
  if (first?.description) return first.description;
  if (first?.code && ERROR_TEXT[first.code]) return ERROR_TEXT[first.code];
  // ModelState hataları: { errors: { Text: ["..."] } }
  const errors = (data as { errors?: Record<string, string[]> })?.errors;
  if (errors) {
    const msg = Object.values(errors).flat()[0];
    if (msg) return msg;
  }
  return fallback;
}
